import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { uploadToIPFS, registerContent } from '../utils/contracts';

function ContentUpload({ wallet, contracts }) {
  const [file, setFile] = useState(null);
  const [contentType, setContentType] = useState('image');
  const [isAIGenerated, setIsAIGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFileChange = event => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setError('');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!wallet || !contracts) {
      setError('Please connect your wallet first');
      return;
    }

    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Upload to IPFS
      const contentHash = await uploadToIPFS(file);

      // Register content on blockchain
      const tx = await registerContent(
        contracts.contentAuth,
        contentHash,
        contentType,
        isAIGenerated
      );

      setSuccess(`Content registered successfully! Transaction: ${tx}`);
      setFile(null);
    } catch (error) {
      console.error('Upload failed:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!wallet) {
    return <Alert severity="info">Please connect your wallet to upload content.</Alert>;
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Upload Content
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <input
            accept="image/*,video/*,audio/*"
            style={{ display: 'none' }}
            id="raised-button-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="raised-button-file">
            <Button variant="contained" component="span" fullWidth>
              Select File
            </Button>
          </label>

          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {file.name}
            </Typography>
          )}

          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Content Type</Typography>
            <RadioGroup value={contentType} onChange={e => setContentType(e.target.value)}>
              <FormControlLabel value="image" control={<Radio />} label="Image" />
              <FormControlLabel value="video" control={<Radio />} label="Video" />
              <FormControlLabel value="audio" control={<Radio />} label="Audio" />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Content Origin</Typography>
            <RadioGroup
              value={isAIGenerated.toString()}
              onChange={e => setIsAIGenerated(e.target.value === 'true')}
            >
              <FormControlLabel value="false" control={<Radio />} label="Human Created" />
              <FormControlLabel value="true" control={<Radio />} label="AI Generated" />
            </RadioGroup>
          </FormControl>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            disabled={loading || !file}
          >
            {loading ? <CircularProgress size={24} /> : 'Upload and Register Content'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default ContentUpload;
